import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription, combineLatest } from 'rxjs';
import { EmployeeService } from 'src/app/services/employee.service';
import { UtilsService } from 'src/app/services/utils.service';

type MainTab = 'datos' | 'historico' | 'salarios' | 'infonavit';
type SubTab = 'general' | 'salario' | 'personal' | 'salud' | 'fiscales' | 'imss';

const ENABLED_MAIN_TABS_ON_CREATE: MainTab[] = ['datos'];
const ENABLED_SUB_TABS_ON_CREATE: SubTab[] = ['general'];

@Component({
    selector: 'app-employee-detail',
    templateUrl: './employee-detail.component.html',
    styleUrls: ['./employee-detail.component.css']
})
export class EmployeeDetailComponent implements OnInit, OnDestroy {
    employeeId!: number;
    employee: any = null;
    isCreateMode = false;

    mainTab: MainTab = 'datos';
    subTab: SubTab = 'general';

    private paramMapSub!: Subscription;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private employeeService: EmployeeService,
        private utils: UtilsService,
        private spinner: NgxSpinnerService
    ) { }

    ngOnInit(): void {
        // Se suscribe (en vez de usar solo el snapshot) para reaccionar cuando
        // Angular reutiliza esta misma instancia al pasar de "new" a ":id"
        // (por ejemplo, tras registrar un colaborador y navegar con replaceUrl).
        //
        // La ruta "new" no tiene segmento ":id", así que paramMap.get('id') es
        // null ahí (nunca el string "new") — el modo crear se determina con la
        // route `data.type`, que sí distingue "new" de ":id" de forma fiable.
        this.paramMapSub = combineLatest([this.route.paramMap, this.route.data]).subscribe(([params, data]) => {
            const idParam = params.get('id');
            const isCreateRoute = data?.['type'] === 'create';

            if (isCreateRoute || idParam === null) {
                this.isCreateMode = true;
                this.employeeId = 0;
                this.employee = null;
                this.mainTab = 'datos';
                this.subTab = 'general';
                return;
            }

            this.isCreateMode = false;
            this.employeeId = parseInt(idParam, 10);
            this.loadEmployee();
        });
    }

    ngOnDestroy(): void {
        this.paramMapSub?.unsubscribe();
    }

    loadEmployee(): void {
        if (!this.employeeId) return;
        this.spinner.show();
        this.employeeService.getById(this.employeeId).subscribe({
            next: (resp: any) => {
                this.employee = resp;
                this.spinner.hide();
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo obtener la información del colaborador', icon: 'error' });
            }
        });
    }

    onEmployeeUpdated(partial: any): void {
        this.employee = { ...this.employee, ...partial };
    }

    isMainTabDisabled(tab: MainTab): boolean {
        return this.isCreateMode && !ENABLED_MAIN_TABS_ON_CREATE.includes(tab);
    }

    isSubTabDisabled(tab: SubTab): boolean {
        return this.isCreateMode && !ENABLED_SUB_TABS_ON_CREATE.includes(tab);
    }

    setMainTab(tab: MainTab): void {
        if (this.isMainTabDisabled(tab)) return;
        this.mainTab = tab;
    }

    setSubTab(tab: SubTab): void {
        if (this.isSubTabDisabled(tab)) return;
        this.subTab = tab;
    }

    goBack(): void {
        this.router.navigateByUrl('/employee/list');
    }

    onPayrollClick(): void {
        if (!this.employeeId) return;
        this.router.navigateByUrl(`/employee/${this.employeeId}/payroll`);
    }

    get fullName(): string {
        if (!this.employee) return '';
        return [this.employee.first_name, this.employee.last_name, this.employee.mother_last_name].filter(Boolean).join(' ');
    }

    get initials(): string {
        if (!this.employee) return '--';
        const a = String(this.employee.first_name || '').charAt(0);
        const b = String(this.employee.last_name || '').charAt(0);
        const value = `${a}${b}`.toUpperCase();
        return value || '--';
    }

    get statusIsActive(): boolean {
        return this.employee?.status !== 'terminated';
    }

    get statusLabel(): string {
        return this.statusIsActive ? 'Alta' : 'Baja';
    }

    get photoUrl(): string | null {
        return this.employeeService.getPhotoUrl(this.employee?.photo);
    }

    formatDate(value: any): string {
        if (!value) return '--/--/----';
        const datePart = String(value).split('T')[0].split(' ')[0];
        const [yyyy, mm, dd] = datePart.split('-');
        if (!yyyy || !mm || !dd) return '--/--/----';
        return `${dd}/${mm}/${yyyy}`;
    }
}
